import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Button,
  Chip,
  alpha,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { useAppNavigation } from '../../hooks/useAppNavigation';

interface ApplicationCardProps {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  link: string;
  stats?: {
    label: string;
    value: string | number;
  };
  isAdmin?: boolean;
  delay?: number;
}

// Pulse animation for icons
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// Fade in animation
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ApplicationCard: React.FC<ApplicationCardProps> = ({
  title,
  subtitle,
  description,
  icon,
  color,
  link,
  stats,
  isAdmin = false,
  delay = 0,
}) => {
  const { navigateTo } = useAppNavigation();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        animation: `${fadeInUp} 0.6s ease-out ${delay}ms both`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: (theme) => theme.shadows[8],
          '& .card-icon': {
            animation: `${pulse} 1s ease infinite`,
          },
          '& .card-gradient': {
            opacity: 1,
          },
          '& .hover-overlay': {
            opacity: 1,
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: color,
          transition: 'height 0.3s ease',
        },
        '&:hover::before': {
          height: '8px',
        },
      }}
      onClick={() => navigateTo(link)}
    >
      {/* Gradient overlay */}
      <Box
        className="card-gradient"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(color, 0.05)} 0%, transparent 100%)`,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
        }}
      />

      <CardContent sx={{ flexGrow: 1, position: 'relative', zIndex: 1 }}>
        {/* Icon and Title Row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
          <Box
            className="card-icon"
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha(color, 0.1),
              color: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              position: 'relative',
              '& svg': {
                fontSize: 32,
              },
            }}
          >
            {icon}
            {isAdmin && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: 'error.main',
                  border: '2px solid white',
                }}
              />
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ fontWeight: 500, mb: 1 }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, lineHeight: 1.6 }}
        >
          {description}
        </Typography>

        {/* Stats */}
        {stats && (
          <Box sx={{ mt: 'auto' }}>
            <Chip
              label={`${stats.label}: ${stats.value}`}
              size="small"
              sx={{
                backgroundColor: alpha(color, 0.1),
                color: color,
                fontWeight: 500,
              }}
            />
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          sx={{
            backgroundColor: color,
            '&:hover': {
              backgroundColor: alpha(color, 0.9),
            },
          }}
        >
          {isAdmin ? 'Open Admin Panel' : 'Explore'}
        </Button>
      </CardActions>

      {/* Hover overlay */}
      <Box
        className="hover-overlay"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: `linear-gradient(to top, ${alpha(color, 0.03)} 0%, transparent 50%)`,
          opacity: 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
        }}
      />
    </Card>
  );
};